/**
 * Resolving a booking's venue name.
 *
 * For almost every Westminster service the JRNI resource *is* the venue —
 * "Fitzrovia Chapel", "Old Marylebone Town Hall - The Soho Room" — so
 * `booking.resource_name` is the right thing to display.
 *
 * A few services instead share a single placeholder resource, because the
 * customer names the venue themselves rather than picking from a list. On
 * staging those placeholders are the resources "Church" (id 155) and
 * "Other Venue" (id 164). For a booking on one of those services
 * `resource_name` renders the placeholder ("Church"), and the real venue is a
 * booking question answer ("AGHIA SOPHIA, MOSCOW ROAD, BAYSWATER").
 *
 * Which services behave that way is configuration, not code:
 *
 *   myCeremony['our-ceremony'].questions.venueNameGlobalServiceSwitch
 *   myCeremony['our-ceremony'].questions.venueName
 *
 * The switch accepts either a single global service id (the shape already in
 * use) or a list, so further placeholder-resource services — the four
 * "Other Venue" services — can be onboarded in config alone.
 *
 * Matching is on the *global* service id, because that is what config holds.
 * A company-scoped `service_id` will never match (48432 vs 48431).
 *
 * NOTE: mirrored in westminster-registrar-ceremonies-booking-service
 * (`services/venue-name.util.ts`), which renders the same venue on its own
 * booking confirmation page. Keep the two in step.
 */

/** The `questions` block of the `our-ceremony` config. */
export interface VenueNameQuestionsConfig {
	/** Global service id(s) whose venue comes from a booking question. */
	venueNameGlobalServiceSwitch?: number | string | Array<number | string> | null;
	/** Id of the booking question holding the venue name. */
	venueName?: number | string | null;
}

/** A JRNI booking question answer, keyed by question id on `booking.questions`. */
export interface BookingQuestionAnswer {
	answer?: string | null;
}

export interface ResolveVenueNameArgs {
	/** `booking.resource_name` — the default, and correct for most services. */
	resourceName?: string | null;
	/** `service.global_id` for the booking's service. */
	serviceGlobalId?: number | string | null;
	/** `booking.questions`, keyed by question id. */
	bookingQuestions?: Record<string, BookingQuestionAnswer> | null;
	/** `config.myCeremony['our-ceremony'].questions`. */
	questionsConfig?: VenueNameQuestionsConfig | null;
}

const normalise = (value: unknown): string =>
	value === null || value === undefined ? '' : String(value).trim();

/**
 * True when this service's venue is named by a booking question rather than by
 * its resource. Tolerates the switch being a single id or a list, and ids
 * arriving from config as strings rather than numbers.
 */
export function isVenueNamedByQuestion(
	serviceGlobalId?: number | string | null,
	questionsConfig?: VenueNameQuestionsConfig | null
): boolean {
	const globalId = normalise(serviceGlobalId);
	if (!globalId) {
		return false;
	}
	const switchValue = questionsConfig?.venueNameGlobalServiceSwitch;
	if (switchValue === null || switchValue === undefined) {
		return false;
	}
	const configured = Array.isArray(switchValue) ? switchValue : [switchValue];
	return configured.some((id) => normalise(id) === globalId);
}

/**
 * The venue name to display for a booking.
 *
 * Falls back to the resource name whenever the service is not question-driven,
 * the question is unconfigured, or the answer is blank — so a missing answer
 * degrades to the placeholder rather than to an empty field.
 */
export function resolveVenueName({
	resourceName,
	serviceGlobalId,
	bookingQuestions,
	questionsConfig,
}: ResolveVenueNameArgs): string {
	const fromResource = normalise(resourceName);

	if (!isVenueNamedByQuestion(serviceGlobalId, questionsConfig)) {
		return fromResource;
	}

	const questionId = normalise(questionsConfig?.venueName);
	if (!questionId || !bookingQuestions) {
		return fromResource;
	}

	const fromQuestion = normalise(bookingQuestions[questionId]?.answer);
	return fromQuestion || fromResource;
}
